/**
 * Tiny IndexedDB key→Blob store used to persist photos that failed to upload
 * (e.g. on a flaky cantiere connection) so the operator doesn't have to retake
 * them. Photos are stored under a synthetic key `pending::<uuid>` which is the
 * same string used as the photo's value inside the form state — so the
 * UploaderCell can transparently render the local preview AND know it still
 * needs to be flushed to the server.
 *
 * Lifecycle:
 *   1) Upload attempt fails after all retries
 *      → savePending(uuid, file) ; cell value becomes `pending::<uuid>`
 *   2) Cell renders the local Blob (via getPendingBlob) with a "in coda" badge
 *   3) On `online` event, on user click, or before form submit:
 *      flushPending() retries each one; on success → removePending + caller
 *      swaps the cell value to the real storage path.
 *
 * No external dependency: uses the raw IndexedDB API.
 */

const DB_NAME = "uc_offline";
const STORE = "pending_photos";
const DB_VERSION = 1;

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB non disponibile"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let out;
    Promise.resolve(fn(store))
      .then((r) => { out = r; })
      .catch(reject);
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export const PENDING_PREFIX = "pending::";

export function isPendingPath(p) {
  return typeof p === "string" && p.startsWith(PENDING_PREFIX);
}

export function newPendingKey() {
  const id = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${PENDING_PREFIX}${id}`;
}

export async function savePending(key, blob, meta = {}) {
  return tx("readwrite", (store) => new Promise((resolve, reject) => {
    const req = store.put({
      key,
      blob,
      mime: blob.type || "image/jpeg",
      name: meta.name || "foto.jpg",
      created_at: Date.now(),
    });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  })).catch(() => null);
}

export async function getPending(key) {
  return tx("readonly", (store) => new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  })).catch(() => null);
}

export async function removePending(key) {
  return tx("readwrite", (store) => new Promise((resolve, reject) => {
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  })).catch(() => null);
}

export async function listPendingKeys() {
  return tx("readonly", (store) => new Promise((resolve, reject) => {
    const req = store.getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  })).catch(() => []);
}

/**
 * Subscribe to a count of pending photos. Returns an unsubscribe function.
 * The internal store is event-based: every save/remove dispatches a window
 * event so multiple components stay in sync without polling.
 */
const EVT = "uc:pending-photos-changed";
export function notifyPendingChanged() {
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* noop */ }
}
export function subscribePendingChanged(cb) {
  const handler = () => cb();
  window.addEventListener(EVT, handler);
  return () => window.removeEventListener(EVT, handler);
}
